<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .sticker-analytics-page
    //- Skeleton — mirrors TicketDetailPage
    template(v-if="loading && !analytics")
      .skeleton-banner
      .skeleton-body
        .skeleton-creator-row
          .skeleton-circle
          .skeleton-bar.skeleton-bar--short
        .skeleton-bar.skeleton-bar--title
        .skeleton-metrics-row
          .skeleton-metric(v-for="n in 4" :key="`metric-${n}`")
            .skeleton-bar.skeleton-bar--xs
            .skeleton-bar.skeleton-bar--short
        .skeleton-divider
        .skeleton-tabs-row
          .skeleton-bar.skeleton-bar--tab
          .skeleton-bar.skeleton-bar--tab
        .skeleton-divider
        .skeleton-bar.skeleton-bar--section-title
        .skeleton-stat-grid
          .skeleton-stat(v-for="n in 4" :key="`stat-${n}`")
        .skeleton-divider
        .skeleton-bar.skeleton-bar--section-title
        .skeleton-bar.skeleton-bar--full

    .error-state(v-else-if="loadError")
      p.error-text Unable to load analytics
      p.error-subtext {{ loadError }}
      button.retry-button(type="button" @click="fetchData") Try again

    template(v-else-if="analytics && stickerMeta")
      //- Landscape event banner (tickets with a dedicated banner image)
      .event-banner(v-if="hasEventBanner")
        button.banner-back-btn(@click="goBack" type="button" aria-label="Go back")
          img(src="@/assets/icons/chevron-right.svg" alt="").back-icon
        img(:src="stickerMeta.bannerUrl" :alt="stickerMeta.title").banner-img

      //- Sticker hero — full artwork visible, no aggressive crop
      .sticker-hero(v-else-if="stickerMeta.url")
        button.banner-back-btn(@click="goBack" type="button" aria-label="Go back")
          img(src="@/assets/icons/chevron-right.svg" alt="").back-icon
        .sticker-hero-inner
          img(:src="stickerMeta.url" :alt="stickerMeta.title").sticker-hero-img

      .page-header(v-else)
        button.banner-back-btn(@click="goBack" type="button" aria-label="Go back")
          img(src="@/assets/icons/chevron-right.svg" alt="").back-icon

      .content-body
        .campaign-badge
          .campaign-badge-dot
          span.campaign-badge-text {{ campaignTypeLabel }}

        .creator-info(v-if="creatorName")
          .creator-avatar
            img(
              v-if="creatorAvatar"
              :src="creatorAvatar"
              :alt="creatorName"
            )
            span.creator-initial(v-else) {{ creatorInitial }}
          .creator-details
            .creator-name {{ creatorName }}

        h2.sticker-title {{ stickerMeta.title }}

        .analytics-metrics-row
          .metric-chip
            img.metric-icon(src="@/assets/icons/eye.svg" alt="Views")
            span.metric-value {{ formatCount(analytics.totalViews) }}
            span.metric-label Views
          .metric-chip
            img.metric-icon(src="@/assets/icons/share.svg" alt="Shares")
            span.metric-value {{ formatCount(analytics.totalShares) }}
            span.metric-label Shares
          .metric-chip
            img.metric-icon(src="@/assets/icons/shopping-cart.svg" alt="Shop")
            span.metric-value {{ formatCount(analytics.totalAddToShop) }}
            span.metric-label Shop
          .metric-chip
            img.metric-icon(src="@/assets/icons/dollar.svg" alt="Raised")
            span.metric-value {{ toplineRaisedValue }}
            span.metric-label {{ toplineRaisedLabel }}

        .analytics-tabs
          button.tab-btn(
            v-for="tab in tabs"
            :key="tab"
            type="button"
            :class="{ active: activeTab === tab }"
            @click="activeTab = tab"
          ) {{ tab }}

        template(v-if="activeTab === 'Overview'")
          .detail-section
            h3.section-heading Summary
            .stat-grid
              .stat-card.accent
                span.stat-label {{ summaryHeroLabel }}
                span.stat-value {{ summaryHeroValue }}
                span.stat-sub All time
              .stat-card
                span.stat-label Total Views
                span.stat-value {{ formatCount(analytics.totalViews) }}
              .stat-card
                span.stat-label Total Shares
                span.stat-value {{ formatCount(analytics.totalShares) }}
              .stat-card
                span.stat-label Add to Shop
                span.stat-value {{ formatCount(analytics.totalAddToShop) }}
              .stat-card
                span.stat-label Total Transactions
                span.stat-value {{ formatCount(analytics.totalTransactions) }}
              .stat-card
                span.stat-label Distinct Buyers
                span.stat-value {{ formatCount(analytics.distinctBuyers) }}

          .detail-section(v-if="analytics.ticket")
            h3.section-heading RSVPs
            p.section-subtitle By ticket type
            .mini-stat-grid
              .mini-stat-card
                .mini-stat-icon 🎟️
                div
                  span.mini-stat-label Total RSVPs
                  span.mini-stat-value {{ formatCount(analytics.ticket.totalRsvps) }}
              .mini-stat-card
                .mini-stat-icon ✅
                div
                  span.mini-stat-label Checked In
                  span.mini-stat-value {{ formatCount(analytics.ticket.checkedInCount) }}
            .tier-list
              .tier-row(v-for="(tier, index) in analytics.ticket.tiers" :key="tier.variantId")
                .tier-header
                  span.tier-name {{ tier.title }}
                  .tier-counts
                    span.tier-count {{ tier.sold.toLocaleString() }}
                    span.tier-pct {{ getTierPercent(tier.sold, analytics.ticket.totalRsvps) }}
                .tier-meta-row
                  span.tier-meta(v-if="tier.capacity != null") Capacity: {{ tier.sold }}/{{ tier.capacity }}
                  span.tier-meta Checked in: {{ tier.checkedIn }}/{{ tier.sold }}
                  span.tier-meta Revenue: {{ formatMoney(tier.revenue) }}
                .progress-track
                  .progress-fill(
                    :class="`tier-color-${index % 4}`"
                    :style="{ width: getTierBarWidth(tier.sold) + '%' }"
                  )

          .detail-section(v-if="analytics.campaign")
            h3.section-heading Campaign
            p.section-subtitle Fundraising breakdown by cause
            .mini-stat-grid
              .mini-stat-card
                .mini-stat-icon 💰
                div
                  span.mini-stat-label Total Raised
                  span.mini-stat-value {{ formatMoney(analytics.campaign.totalRaised) }}
              .mini-stat-card
                .mini-stat-icon #
                div
                  span.mini-stat-label Transactions
                  span.mini-stat-value {{ analytics.campaign.totalTransactions }}
            .cause-list
              .cause-row(v-for="cause in analytics.campaign.causes" :key="cause.causeId")
                .cause-header
                  span.cause-name {{ cause.name }}
                  .cause-stats
                    span.cause-amount {{ formatMoney(cause.amount) }}
                    span.cause-separator ·
                    span.cause-transactions {{ cause.transactions }}
                    span.cause-pct {{ getCausePercent(cause.amount) }}
                .progress-track
                  .progress-fill.campaign-fill(
                    :style="{ width: getCauseBarWidth(cause.amount) + '%' }"
                  )

          .detail-section
            h3.section-heading Advocate Impact
            .hero-stat-card
              .hero-stat-icon 👥
              div
                span.hero-stat-label Raised through advocates
                span.hero-stat-value {{ formatMoney(analytics.advocateRaised) }}
                span.hero-stat-sub {{ formatCount(analytics.advocateTransactions) }} transactions

          .detail-section(v-if="analytics.sweepstakes")
            h3.section-heading Sweepstakes
            SweepstakesWinnersPanel(
              :sticker-id="stickerMeta.id"
              :sweepstakes="sweepstakesPanelData"
              @refreshed="fetchData"
            )

        template(v-else)
          .detail-section
            h3.section-heading Age Demographics
            p.section-subtitle Based on buyers of this sticker

            .audience-empty(v-if="analytics.audience.isSuppressed")
              .empty-icon 📊
              h4.empty-heading Not enough data yet
              p.empty-copy Age demographics are shown once at least five buyers have a known age.

            .audience-chart(v-else)
              .audience-row(v-for="row in analytics.audience.buckets" :key="row.bucket")
                span.audience-range {{ row.bucket }}
                .progress-track.audience-track
                  .progress-fill.audience-fill(
                    :style="{ width: getAudienceBarWidth(row.count) + '%' }"
                  )
                span.audience-pct {{ getAudiencePercent(row.count) }}
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import SweepstakesWinnersPanel from '@/components/SweepstakesWinnersPanel.vue';
import gqlRequest from '../dashboard/gql-request';
import { makeRequest } from '@/graphs/index';
import {
  findSticker,
  findStickerBySlug,
  getStickerAnalytics,
} from '@/graphs/gql';
import isUUID from '@/utils/isUUID';
import { useStickerHostCheck } from '@/composables/useStickerHostCheck';
import { isEmbeddedRoute } from '@/utils/embeddedUtils';
import {
  formatCountNoDecimal,
  formatMoneyAdaptive,
} from '@/views/web-app/glow/utils/numberFormatting';
import type {
  StickerAnalytics,
  StickerAnalyticsStickerMeta,
} from './types/stickerAnalytics.types';

type AnalyticsTab = 'Overview' | 'Audience';

export default defineComponent({
  name: 'StickerAnalytics',
  components: { WebAppLayout, SweepstakesWinnersPanel },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { loadMyOrganizations } = gqlRequest();
    const {
      ensureMyOrganizationsLoaded,
      isHostOfSticker,
    } = useStickerHostCheck(store, loadMyOrganizations);

    const slugorid = String(route.params.slugorid);
    const loading = ref(false);
    const loadError = ref('');
    const analytics = ref<StickerAnalytics | null>(null);
    const stickerMeta = ref<StickerAnalyticsStickerMeta | null>(null);
    const activeTab = ref<AnalyticsTab>('Overview');
    const tabs: AnalyticsTab[] = ['Overview', 'Audience'];

    function isTicketSticker(data: StickerAnalytics): boolean {
      return data.campaignType === 'Ticket' || !!data.ticket;
    }

    function getPrimaryRevenueValue(data: StickerAnalytics): number {
      if (isTicketSticker(data)) {
        return data.grossRevenue;
      }
      if (data.campaign) {
        return data.totalRaised;
      }
      return data.grossRevenue;
    }

    function isStickerOwner(meta: StickerAnalyticsStickerMeta): boolean {
      return isHostOfSticker({
        entity: meta.entity,
        entityId: meta.entityId,
        organization: meta.organization,
        user: meta.user,
      });
    }

    function getStickerDetailPath(meta: StickerAnalyticsStickerMeta): string {
      const slugOrId = meta.slug || meta.id;
      return isEmbeddedRoute(route.path)
        ? `/web/embed/sticker/${slugOrId}`
        : `/web/sticker/${slugOrId}`;
    }

    function redirectToStickerDetail(meta: StickerAnalyticsStickerMeta) {
      router.replace(getStickerDetailPath(meta));
    }

    const maxTicketSold = computed(() => {
      if (!analytics.value?.ticket?.tiers?.length) return 0;
      return Math.max(...analytics.value.ticket.tiers.map((tier) => tier.sold));
    });

    const maxCauseAmount = computed(() => {
      if (!analytics.value?.campaign?.causes?.length) return 0;
      return Math.max(...analytics.value.campaign.causes.map((cause) => cause.amount));
    });

    const maxAudienceCount = computed(() => {
      if (!analytics.value?.audience?.buckets?.length) return 0;
      return Math.max(...analytics.value.audience.buckets.map((row) => row.count));
    });

    const campaignTypeLabel = computed(() => {
      const type = analytics.value?.campaignType || stickerMeta.value?.campaignType;
      return type ? type.toUpperCase() : 'ANALYTICS';
    });

    const creatorName = computed(() => {
      const meta = stickerMeta.value;
      if (!meta) return '';
      if (meta.organization?.name) return meta.organization.name;
      if (meta.user?.firstname || meta.user?.lastname) {
        return `${meta.user.firstname || ''} ${meta.user.lastname || ''}`.trim();
      }
      return '';
    });

    const creatorAvatar = computed(
      () => stickerMeta.value?.organization?.avatar || stickerMeta.value?.user?.avatar || '',
    );

    const creatorInitial = computed(() => {
      const name = creatorName.value.trim();
      return name ? name.charAt(0).toUpperCase() : '?';
    });

    const hasEventBanner = computed(() => !!stickerMeta.value?.bannerUrl);

    const isTicketAnalytics = computed(() => (
      analytics.value ? isTicketSticker(analytics.value) : false
    ));

    const summaryHeroLabel = computed(() => (
      isTicketAnalytics.value ? 'Gross Revenue' : 'Total Raised'
    ));

    const toplineRaisedLabel = computed(() => (
      isTicketAnalytics.value ? 'Revenue' : 'Raised'
    ));

    const summaryHeroValue = computed(() => {
      if (!analytics.value) return '$0';
      return formatMoney(getPrimaryRevenueValue(analytics.value));
    });

    const toplineRaisedValue = computed(() => {
      if (!analytics.value) return '$0';
      return formatMoney(getPrimaryRevenueValue(analytics.value));
    });

    function formatCount(value: number): string {
      return formatCountNoDecimal(Number(value) || 0);
    }

    function formatMoney(value: number): string {
      return formatMoneyAdaptive(Number(value) || 0);
    }

    function getTierPercent(sold: number, total: number): string {
      if (!total) return '0.0%';
      return `${((sold / total) * 100).toFixed(1)}%`;
    }

    function getTierBarWidth(sold: number): number {
      if (!maxTicketSold.value) return 0;
      return (sold / maxTicketSold.value) * 100;
    }

    function getCausePercent(amount: number): string {
      const total = analytics.value?.campaign?.totalRaised || 0;
      if (!total) return '0.0%';
      return `${((amount / total) * 100).toFixed(1)}%`;
    }

    function getCauseBarWidth(amount: number): number {
      if (!maxCauseAmount.value) return 0;
      return (amount / maxCauseAmount.value) * 100;
    }

    function getAudiencePercent(count: number): string {
      const total = analytics.value?.audience?.totalBuyers || 0;
      if (!total) return '0%';
      return `${((count / total) * 100).toFixed(1)}%`;
    }

    function getAudienceBarWidth(count: number): number {
      if (!maxAudienceCount.value) return 0;
      return (count / maxAudienceCount.value) * 100;
    }

    const sweepstakesPanelData = computed(() => {
      const sweepstakes = analytics.value?.sweepstakes;
      if (!sweepstakes) return null;

      return {
        status: stickerMeta.value?.status,
        isWinnerConfirmed: sweepstakes.isWinnerConfirmed,
        drawingDateTime: sweepstakes.drawingDateTime,
        drawnAt: sweepstakes.drawnAt,
        losersNotifiedAt: sweepstakes.losersNotifiedAt,
        numberOfWinners: sweepstakes.numberOfWinners,
        winners: (sweepstakes.winners || []).map((winner) => ({
          id: winner.id,
          name: winner.name,
          avatar: winner.avatar,
          selectedAt: winner.selectedAt,
          notifiedAt: winner.notifiedAt,
        })),
      };
    });

    async function fetchStickerMeta(): Promise<StickerAnalyticsStickerMeta | null> {
      let sticker: any = null;

      if (isUUID(slugorid)) {
        const byId = await makeRequest('query', findSticker, { id: slugorid }, null);
        sticker = byId?.data?.findSticker;
        if (!sticker) {
          const bySlug = await makeRequest(
            'query',
            findStickerBySlug,
            { slug: slugorid },
            null,
          );
          sticker = bySlug?.data?.findStickerBySlug;
        }
      } else {
        const bySlug = await makeRequest(
          'query',
          findStickerBySlug,
          { slug: slugorid },
          null,
        );
        sticker = bySlug?.data?.findStickerBySlug;
      }

      if (!sticker) return null;

      return {
        id: sticker.id,
        title: sticker.title,
        url: sticker.url,
        bannerUrl: sticker.bannerUrl,
        slug: sticker.slug,
        entity: sticker.entity,
        entityId: sticker.entityId,
        campaignType: sticker.campaignType,
        status: sticker.status,
        organization: sticker.organization,
        user: sticker.user,
      };
    }

    async function fetchAnalytics(stickerId: string, orgId?: string) {
      const variables: { stickerId: string; orgId?: string } = { stickerId };
      if (orgId) variables.orgId = orgId;

      const response = await makeRequest(
        'query',
        getStickerAnalytics,
        variables,
        null,
      );

      if (response?.errors?.length) {
        const message = response.errors[0]?.message || 'Failed to load analytics';
        const isAuthError = /unauthorized|forbidden|not authorized|permission/i.test(message);
        if (isAuthError) {
          throw Object.assign(new Error(message), { isAuthError: true });
        }
        throw new Error(message);
      }

      return response?.data?.getStickerAnalytics as StickerAnalytics | null;
    }

    async function fetchData() {
      loading.value = true;
      loadError.value = '';

      try {
        const meta = await fetchStickerMeta();
        if (!meta?.id) {
          throw new Error('Sticker not found');
        }

        await ensureMyOrganizationsLoaded();

        if (!isStickerOwner(meta)) {
          redirectToStickerDetail(meta);
          return;
        }

        stickerMeta.value = meta;

        const orgId = meta.entity === 'Organization'
          ? meta.entityId || store.getters['actingEntity/orgId']
          : undefined;

        analytics.value = await fetchAnalytics(meta.id, orgId);
        if (!analytics.value) {
          throw new Error('No analytics data returned');
        }
      } catch (err: any) {
        if (err?.isAuthError) {
          const meta = stickerMeta.value || await fetchStickerMeta().catch(() => null);
          if (meta?.id) {
            redirectToStickerDetail(meta);
            return;
          }
        }

        console.error('Failed to load sticker analytics:', err);
        loadError.value = err?.message || 'Failed to load analytics';
        ElMessage.error(loadError.value);
      } finally {
        loading.value = false;
      }
    }

    function goBack() {
      if (stickerMeta.value?.id) {
        router.push(getStickerDetailPath(stickerMeta.value));
        return;
      }
      router.back();
    }

    onMounted(fetchData);

    return {
      loading,
      loadError,
      analytics,
      stickerMeta,
      activeTab,
      tabs,
      campaignTypeLabel,
      creatorName,
      creatorAvatar,
      creatorInitial,
      hasEventBanner,
      toplineRaisedValue,
      toplineRaisedLabel,
      summaryHeroLabel,
      summaryHeroValue,
      formatCount,
      formatMoney,
      getTierPercent,
      getTierBarWidth,
      getCausePercent,
      getCauseBarWidth,
      getAudiencePercent,
      getAudienceBarWidth,
      sweepstakesPanelData,
      fetchData,
      goBack,
    };
  },
});
</script>

<style lang="scss" scoped>
// ── Page wrapper — matches TicketDetailPage ──
.sticker-analytics-page {
  padding-top: 0 !important;
  padding-left: 24px !important;
  padding-right: 32px !important;
  padding-bottom: 24px !important;
  max-width: 100%;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  min-height: 100vh;

  @media (min-width: 768px) {
    padding-left: 40px !important;
    padding-right: 48px !important;
    padding-bottom: 32px !important;
  }

  @media (min-width: 1024px) {
    padding-left: 64px !important;
    padding-right: 72px !important;
    padding-bottom: 40px !important;
  }
}

// ── Hero banner — full bleed (landscape event banners only) ──
.event-banner {
  margin-left: -24px !important;
  margin-right: -32px !important;
  max-height: 280px;
  overflow: hidden;
  margin-bottom: 24px;
  position: relative;

  @media (min-width: 768px) {
    margin-left: -40px !important;
    margin-right: -48px !important;
  }

  @media (min-width: 1024px) {
    margin-left: -64px !important;
    margin-right: -72px !important;
  }

  .banner-img {
    width: 100%;
    height: 100%;
    min-height: 220px;
    object-fit: cover;
    display: block;
  }
}

// ── Sticker hero — shows full artwork without cropping ──
.sticker-hero {
  margin-left: -24px !important;
  margin-right: -32px !important;
  margin-bottom: 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem 2rem;
  min-height: 200px;
  background: linear-gradient(180deg, #f0fdfa 0%, #f9fafb 60%, #fff 100%);

  @media (min-width: 768px) {
    margin-left: -40px !important;
    margin-right: -48px !important;
    padding: 3.5rem 2rem 2.5rem;
    min-height: 260px;
  }

  @media (min-width: 1024px) {
    margin-left: -64px !important;
    margin-right: -72px !important;
    min-height: 300px;
  }
}

.sticker-hero-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 220px;

  @media (min-width: 768px) {
    max-height: 260px;
  }

  @media (min-width: 1024px) {
    max-height: 280px;
  }
}

.sticker-hero-img {
  max-height: 220px;
  max-width: min(320px, 70vw);
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.1));

  @media (min-width: 768px) {
    max-height: 260px;
    max-width: min(360px, 50vw);
  }

  @media (min-width: 1024px) {
    max-height: 280px;
    max-width: min(400px, 40vw);
  }
}

.page-header {
  position: relative;
  margin-bottom: 16px;
  min-height: 2.25rem;
}

.banner-back-btn {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);

  &:hover {
    background: #fff;
  }
}

.back-icon {
  width: 1.125rem;
  height: 1.125rem;
  transform: rotate(180deg);
}

// ── Content body ──
.content-body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.campaign-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ccfbf1;
  color: #0f766e;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  width: fit-content;
  margin-bottom: 16px;

  .campaign-badge-dot {
    width: 8px;
    height: 8px;
    background: #14b8a6;
    border-radius: 50%;
  }
}

.creator-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.creator-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .creator-initial {
    color: white;
    font-weight: 700;
    font-size: 20px;
  }
}

.creator-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.creator-name {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.sticker-title {
  font-size: 22px;
  font-weight: 900;
  color: #1a1a1a;
  margin: 4px 0 16px;
  line-height: 1.2;

  @media (min-width: 480px) {
    font-size: 26px;
  }

  @media (min-width: 768px) {
    font-size: 30px;
  }
}

.analytics-metrics-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 20px;

  @media (max-width: 479px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.metric-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px;
  background: #f9fafb;
  border-radius: 12px;
}

.metric-icon {
  width: 1rem;
  height: 1rem;
  opacity: 0.75;
}

.metric-value {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.1;
}

.metric-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6b7280;
}

.analytics-tabs {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
}

.tab-btn {
  background: none;
  border: none;
  padding: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;

  &:hover {
    color: #1a1a1a;
  }

  &.active {
    color: #0d9488;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 2px;
      background: #0d9488;
      border-radius: 9999px;
    }
  }
}

.detail-section {
  padding: 20px 0;
  border-top: 1px solid #f3f4f6;
}

.section-heading {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px;
}

.section-subtitle {
  margin: -8px 0 16px;
  font-size: 13px;
  color: #6b7280;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.stat-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.accent {
    background: #ecfdf5;

    .stat-value {
      color: #0f766e;
    }
  }
}

.stat-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.1;
}

.stat-sub {
  font-size: 12px;
  color: #6b7280;
}

.hero-stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.hero-stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.hero-stat-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.hero-stat-value {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
}

.hero-stat-sub {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.tier-list,
.cause-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tier-header,
.cause-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.tier-name,
.cause-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 55%;
}

.tier-counts,
.cause-stats {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.tier-count,
.cause-amount {
  font-weight: 700;
  color: #1a1a1a;
}

.tier-pct,
.cause-pct,
.cause-transactions {
  color: #6b7280;
}

.tier-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.tier-meta {
  font-size: 11px;
  color: #6b7280;
}

.cause-separator {
  color: #d1d5db;
}

.progress-track {
  height: 8px;
  background: #ececf0;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 9999px;
  background: #14b8a6;
  transition: width 0.4s ease;
}

.tier-color-0 { background: #0d9488; }
.tier-color-1 { background: #14b8a6; }
.tier-color-2 { background: #2dd4bf; }
.tier-color-3 { background: #5eead4; }

.campaign-fill,
.audience-fill {
  background: #14b8a6;
}

.mini-stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.mini-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
}

.mini-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mini-stat-label {
  display: block;
  font-size: 11px;
  color: #6b7280;
}

.mini-stat-value {
  display: block;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
}

.winner-card {
  display: flex;
  gap: 12px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  border-radius: 12px;
  padding: 16px;
}

.winner-avatar {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.winner-icon {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.winner-name {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.winner-meta,
.winner-date {
  display: block;
  font-size: 12px;
  color: #6b7280;
}

.winner-prize {
  display: inline-flex;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
}

.audience-empty {
  text-align: center;
  padding: 24px 8px;
}

.empty-heading {
  margin: 8px 0 4px;
  color: #1a1a1a;
}

.empty-copy {
  color: #6b7280;
  margin: 0;
  font-size: 14px;
}

.audience-chart {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.audience-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.audience-range {
  width: 3rem;
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  flex-shrink: 0;
}

.audience-track {
  flex: 1;
}

.audience-pct {
  width: 2.5rem;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: #1a1a1a;
  flex-shrink: 0;
}

.error-state {
  padding: 48px 16px;
  text-align: center;
}

.error-text {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px;
}

.error-subtext {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px;
}

.retry-button {
  border: none;
  border-radius: 9999px;
  background: #0d9488;
  color: #fff;
  font-weight: 600;
  padding: 12px 24px;
  cursor: pointer;

  &:hover {
    background: #0f766e;
  }
}

// ── Skeleton — matches TicketDetailPage ──
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@mixin shimmer-bg {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 6px;
}

.skeleton-banner {
  @include shimmer-bg;
  height: 220px;
  border-radius: 0 !important;
  margin-left: -24px !important;
  margin-right: -32px !important;
  margin-top: 0;
  margin-bottom: 24px;
  background: linear-gradient(180deg, #f0fdfa 0%, #f9fafb 60%, #fff 100%);

  @media (min-width: 768px) {
    height: 260px;
    margin-left: -40px !important;
    margin-right: -48px !important;
  }

  @media (min-width: 1024px) {
    height: 300px;
    margin-left: -64px !important;
    margin-right: -72px !important;
  }

  &::after {
    content: '';
    display: block;
    width: min(280px, 50vw);
    height: min(200px, 70%);
    margin: 2.5rem auto 0;
    @include shimmer-bg;
    border-radius: 12px;
  }
}

.skeleton-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-circle {
  @include shimmer-bg;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-creator-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.skeleton-bar {
  @include shimmer-bg;
  height: 14px;

  &.skeleton-bar--short { width: 40%; }
  &.skeleton-bar--full { width: 100%; }
  &.skeleton-bar--xs { width: 20%; }

  &.skeleton-bar--title {
    height: 32px;
    width: 75%;
    margin-bottom: 4px;
  }

  &.skeleton-bar--section-title {
    height: 18px;
    width: 35%;
    margin-bottom: 8px;
  }

  &.skeleton-bar--tab {
    flex: 1;
    height: 32px;
  }
}

.skeleton-metrics-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 479px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.skeleton-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  background: #f9fafb;
  border-radius: 12px;
}

.skeleton-tabs-row {
  display: flex;
  gap: 16px;
}

.skeleton-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 6px 0;
}

.skeleton-stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.skeleton-stat {
  @include shimmer-bg;
  height: 88px;
  border-radius: 12px;
}
</style>
